<template>
  <section class="payment-methods-panel section-card">
    <header class="section-card__header">
      <div>
        <h2 class="section-card__title">
          <i class="fa-regular fa-credit-card section-card__icon" />
          {{ t('billing.subscriptionPage.paymentMethods.title') }}
        </h2>
        <p class="section-card__subtitle">
          {{ t('billing.subscriptionPage.paymentMethods.subtitle') }}
        </p>
      </div>
      <Button
        :label="t('billing.subscriptionPage.paymentMethods.actions.add')"
        icon="fa-regular fa-plus"
        class="p-button-sm p-button-outlined"
        @click="$emit('add')"
      />
    </header>

    <TransitionGroup
      v-if="paymentMethods.items.length > 0"
      name="payment-methods-panel-list"
      tag="ul"
      class="payment-methods-panel__list"
    >
      <li
        v-for="item in paymentMethods.items"
        :key="item.id"
        class="payment-methods-panel__item"
        :class="{
          'payment-methods-panel__item--expired': item.isExpired,
          'payment-methods-panel__item--just-default': item.id === recentlyDefaultedId,
        }"
      >
        <div class="payment-methods-panel__item-icon">
          <i :class="brandIcon(item.brand)" />
        </div>
        <div class="payment-methods-panel__item-body">
          <div class="payment-methods-panel__item-title">
            {{ item.brand }} ····  {{ item.last4 }}
            <span
              v-if="item.isDefault"
              class="payment-methods-panel__badge"
            >
              {{ t('billing.subscriptionPage.paymentMethods.default') }}
            </span>
            <span
              v-if="item.isExpired"
              class="payment-methods-panel__badge payment-methods-panel__badge--danger"
            >
              {{ t('billing.subscriptionPage.paymentMethods.expired') }}
            </span>
          </div>
          <div class="payment-methods-panel__item-meta">
            {{ item.ownerName }} · {{ t('billing.subscriptionPage.paymentMethods.expires') }} {{ item.expiry }}
          </div>
        </div>
        <div class="payment-methods-panel__item-actions">
          <Button
            v-if="!item.isDefault && !item.isExpired"
            :label="t('billing.subscriptionPage.paymentMethods.actions.setDefault')"
            class="p-button-text p-button-sm"
            @click="$emit('set-default', item.id)"
          />
          <Button
            v-if="!item.isDefault"
            icon="fa-regular fa-trash"
            class="p-button-text p-button-sm p-button-danger"
            :title="t('billing.subscriptionPage.paymentMethods.actions.delete')"
            @click="$emit('delete', item.id)"
          />
        </div>
      </li>
    </TransitionGroup>

    <div
      v-else
      class="payment-methods-panel__empty"
    >
      <i class="fa-regular fa-credit-card payment-methods-panel__empty-icon" />
      <div class="payment-methods-panel__empty-text">
        {{ t('billing.subscriptionPage.paymentMethods.empty') }}
      </div>
      <Button
        :label="t('billing.subscriptionPage.paymentMethods.actions.add')"
        icon="fa-regular fa-plus"
        class="p-button-sm mt-3"
        @click="$emit('add')"
      />
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import { PaymentMethodsSection } from '@/types/billing-types';

export default defineComponent({
  name: 'PaymentMethodsPanel',

  components: { Button },

  props: {
    paymentMethods: {
      type: Object as PropType<PaymentMethodsSection>,
      required: true,
    },
    /**
     * Token id that just became default — flashes a brand-colour pulse to
     * draw the eye to the new default after the list reorders. The parent
     * clears this back to `null` ~1.4s later.
     */
    recentlyDefaultedId: {
      type: String as PropType<string | null>,
      default: null,
    },
  },

  emits: ['add', 'set-default', 'delete'],

  setup() {
    const { t } = useI18n();

    const brandIcon = (brand: string): string => {
      const b = (brand ?? '').toLowerCase();
      if (b.includes('visa')) return 'fa-brands fa-cc-visa';
      if (b.includes('master')) return 'fa-brands fa-cc-mastercard';
      if (b.includes('amex')) return 'fa-brands fa-cc-amex';
      if (b.includes('discover')) return 'fa-brands fa-cc-discover';
      return 'fa-regular fa-credit-card';
    };

    return { t, brandIcon };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.payment-methods-panel {
  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 16px;
    background: #fff;
    border: 1px solid #eaeaea;
    border-radius: 12px;
    transition: border-color 120ms ease, background 120ms ease, box-shadow 240ms ease;

    &:hover {
      border-color: #d1d5db;
      background: #fcfcfc;
    }

    &--expired {
      opacity: 0.6;
    }

    &--just-default {
      animation: payment-methods-panel-pulse 1400ms cubic-bezier(0.22, 1, 0.36, 1);
    }
  }

  &__item-icon {
    width: 48px;
    height: 36px;
    background: #f5f6f8;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: #1a1f36;
    flex-shrink: 0;
  }

  &__item-body {
    flex: 1;
    min-width: 0;
  }

  &__item-title {
    font-weight: 600;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__item-meta {
    font-size: 0.8125rem;
    color: #6b7280;
    margin-top: 2px;
  }

  &__item-actions {
    display: flex;
    gap: 4px;
    align-items: center;
    flex-shrink: 0;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 100px;
    font-size: 0.6875rem;
    font-weight: 600;
    background: rgba(148, 200, 64, 0.15);
    color: #629d03;
    letter-spacing: 0.02em;

    &--danger {
      background: rgba(239, 68, 68, 0.12);
      color: #b91c1c;
    }
  }

  &__empty {
    padding: 40px 20px;
    text-align: center;
    border: 1px dashed #e5e7eb;
    border-radius: 12px;
  }

  &__empty-icon {
    font-size: 2rem;
    color: #d1d5db;
    margin-bottom: 8px;
  }

  &__empty-text {
    color: #6b7280;
    font-size: 0.9rem;
  }
}

// ─── List reorder transitions ─────────────────────────────────────────────
// Vue's <TransitionGroup> applies these classes when an item is added,
// removed, or moved (due to a key reorder when the default card changes).
// `-move` does the slide ; `-enter-active`/`-leave-active` keep things
// graceful if a token ever appears / disappears mid-session.
.payment-methods-panel-list-move {
  transition: transform 420ms cubic-bezier(0.22, 1, 0.36, 1);
}

.payment-methods-panel-list-enter-active,
.payment-methods-panel-list-leave-active {
  transition: opacity 240ms ease, transform 240ms ease;
}

.payment-methods-panel-list-enter-from,
.payment-methods-panel-list-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

// Required by <TransitionGroup> when leaving items overlap with moving ones:
// take the leaving item out of the flow so the others can slide into place.
.payment-methods-panel-list-leave-active {
  position: absolute;
  width: 100%;
}

@keyframes payment-methods-panel-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(148, 200, 64, 0.55), 0 0 0 0 rgba(148, 200, 64, 0);
    border-color: $brand-color-primary;
  }
  40% {
    box-shadow: 0 0 0 6px rgba(148, 200, 64, 0.18), 0 4px 14px -3px rgba(148, 200, 64, 0.4);
    border-color: $brand-color-primary;
  }
  100% {
    box-shadow: 0 0 0 0 rgba(148, 200, 64, 0), 0 0 0 0 rgba(148, 200, 64, 0);
    border-color: #eaeaea;
  }
}
</style>
